import type { ContainerProps } from '@hover/blueprint';
import {
  DesignSystemProvider,
  LoadingOverlay,
  VStack,
  useDisclosure,
} from '@hover/blueprint';
import { breakpoints } from '@hover/blueprint/foundation';
import React, { useEffect } from 'react';
import { Redirect } from 'react-router';

import { Footer } from 'src/modules/pro/components/Footer';
import { Header } from 'src/modules/pro/components/Header';
import { HeroSection } from 'src/modules/pro/components/HeroSection';
import { ImageGrid } from 'src/modules/pro/components/ImageGrid';
import { LeadFormSection } from 'src/modules/pro/components/LeadFormSection';
import { VideoSection } from 'src/modules/pro/components/VideoSection';
import { useProPageContext } from 'src/modules/pro/hooks/useProPageContext';
import { ProPageProvider } from 'src/modules/pro/providers/ProPageProvider';
import { Title as MetaTitle } from 'src/shared/components/Meta/Title/index';
import { SetIntercomVisibility } from 'src/shared/components/SetIntercomVisibility';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';
import { TranslationNamespace } from 'src/shared/i18n/types';
import { Route as Routes } from 'src/shared/route';
import {
  imageLoaded,
  pageViewed,
} from 'src/shared/telemetry/generated/segment';

import leadLandingPageVideoDesktop from 'assets/video/designer-landing-landscape.mp4';
import leadLandingPageVideoMobile from 'assets/video/designer-landing-portrait.mp4';

import type { SampleImageMeta } from './ImageGrid/utils';
import { PhotoLabelSheet } from './PhotoLabelSheet';
import { PhotoUploadSheet } from './PhotoUploadSheet';
import { ProPageSheet } from './ProPageSheet/ProPageSheet';

const Container = ({ children, ...rest }: ContainerProps) => (
  <VStack
    alignItems="center"
    height="100%"
    margin="0 auto"
    maxWidth={breakpoints.width500}
    {...rest}
  >
    {children}
  </VStack>
);

export const ProPageWithProvider = () => {
  const {
    isOpen: proPageSheetIsOpen,
    onOpen: proPageSheetOnOpen,
    onClose: proPageSheetOnClose,
  } = useDisclosure();
  const {
    isOpen: photoUploadSheetIsOpen,
    onOpen: photoUploadSheetOnOpen,
    onClose: photoUploadSheetOnClose,
  } = useDisclosure();

  const {
    loading,
    brandLogoUrl,
    orgDisplayName,
    orgId,
    loggedIn,
    leadSubmitted,
    publicIdentifier,
    genericTrackingData,
    headerOffsetControls,
    spaceTypes,
    styles,
    currentUser,
  } = useProPageContext();
  const { setAffOrgPublicId } = useGlobalStorageContext();

  useEffect(() => {
    setAffOrgPublicId(publicIdentifier);
  }, [publicIdentifier, setAffOrgPublicId]);

  useEffect(() => {
    if (loading) return; // We only want to send a pageViewed event after the data has loaded properly, otherwise we end up with bad data.
    pageViewed({
      ...genericTrackingData,
      track_id: 'lead-landing-page-view-v1',
    });
    // We only want to fire one pageViewed event to avoid noise, and we need it to have the correct data, hence the early terminate clause.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const trackImageLoaded = (sampleImages: SampleImageMeta[]) => {
    sampleImages.forEach(({ style, spaceType: space_type, imgUrl }) => {
      imageLoaded({
        ...genericTrackingData,
        style,
        space_type,
        sample_image_path: imgUrl,
        track_id: 'lead-landing-sample-images',
        user_id: currentUser?.id,
      });
    });
  };

  if (loading) {
    return <LoadingOverlay color="neutral.900" isLoading={loading} />;
  }

  const handleOnTryClick = () => {
    if (leadSubmitted && loggedIn) {
      photoUploadSheetOnOpen();
    } else {
      proPageSheetOnOpen();
    }
  };

  if (brandLogoUrl && orgDisplayName && orgId) {
    return (
      <DesignSystemProvider>
        <Container>
          <SetIntercomVisibility visible={false} />
          <MetaTitle overwrite title={`${orgDisplayName} | Hover`} />
          <Header />
          <HeroSection
            ctaTrackingData={{
              ...genericTrackingData,
              button_location: 'top',
              track_id: 'lead-landing-try-v1',
              primary_cta: true,
            }}
            onTryClick={handleOnTryClick}
            translationName={TranslationNamespace.LEAD_LANDING_PAGE}
          />
          <ProPageSheet
            isOpen={proPageSheetIsOpen}
            onClose={proPageSheetOnClose}
            photoUploadSheetOnOpen={photoUploadSheetOnOpen}
          />
          <PhotoUploadSheet
            isOpen={photoUploadSheetIsOpen}
            onClose={photoUploadSheetOnClose}
          />
          <PhotoLabelSheet />
          {headerOffsetControls && (
            <ImageGrid
              headerOffsetControls={headerOffsetControls}
              onTryItCardClick={handleOnTryClick}
              spaceTypes={spaceTypes}
              styleSelectorIsSticky
              styles={styles}
              trackImageLoaded={trackImageLoaded}
              trackingData={genericTrackingData}
            />
          )}
          <VideoSection
            ctaTrackingData={{
              ...genericTrackingData,
              track_id: 'lead-landing-try-v1',
              button_location: 'video',
              primary_cta: true,
            }}
            desktopVideoSource={leadLandingPageVideoDesktop}
            mobileVideoSource={leadLandingPageVideoMobile}
            onAddPhotoClick={handleOnTryClick}
            primaryBackgroundColor="neutral.800"
            secondaryBackgroundColor="rgba(0, 0, 0, 0.24)"
            translationName={TranslationNamespace.LEAD_LANDING_PAGE}
          />
          <LeadFormSection />
          <Footer />
        </Container>
      </DesignSystemProvider>
    );
  }

  return (
    <Redirect from={Routes.PRO.path} to={Routes.HOMEOWNER_APP_HOME.path} />
  );
};

export const ProPage = () => (
  <ProPageProvider>
    <ProPageWithProvider />
  </ProPageProvider>
);
